import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Lightning,
  IdentificationCard,
  Bank,
  PiggyBank,
  CheckCircle,
  Sparkle,
  Train,
  AirplaneTakeoff,
  Bus,
  Fingerprint,
  Money,
  CreditCard,
  QrCode,
  Coins,
  HandCoins,
  Buildings,
  ShieldCheck,
  Receipt,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import PageHero from "../components/site/PageHero";
import { ACTIVE_SERVICES, UPCOMING_SERVICES } from "../data/site";

const iconMap = {
  Phone,
  Lightning,
  IdentificationCard,
  Bank,
  PiggyBank,
  Train,
  AirplaneTakeoff,
  Bus,
  Fingerprint,
  Money,
  CreditCard,
  QrCode,
  Coins,
  HandCoins,
  Buildings,
  ShieldCheck,
  Receipt,
  PaperPlaneTilt,
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Services Catalogue"
        title="Every fintech service your"
        highlight="customers ask for"
        description="Five services live today across recharge, bills, PAN, banking and savings — many more on the roadmap. Each is built end-to-end inside the retailer app."
        testId="services-hero"
      />

      {/* Active services — detailed cards */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-2">
                Currently Live
              </div>
              <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl tracking-tight">
                Five services in production.
              </h2>
            </div>
            <div className="hidden md:block text-xs uppercase tracking-[0.18em] font-semibold text-ink500">
              {ACTIVE_SERVICES.length} / {ACTIVE_SERVICES.length + UPCOMING_SERVICES.length}
            </div>
          </div>

          <div className="space-y-4">
            {ACTIVE_SERVICES.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Phone;
              return (
                <motion.div
                  key={svc.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.04 }}
                  data-testid={`services-card-${svc.id}`}
                  className="group grid lg:grid-cols-12 gap-6 lg:items-start bg-surfaceAlt border border-line rounded-3xl p-6 sm:p-8 hover:border-brandBlue/40 hover:shadow-lg transition-all"
                >
                  <div className="lg:col-span-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brandBlue/20 to-brandBlue/5 border border-brandBlue/20 flex items-center justify-center">
                        <Icon size={22} weight="duotone" className="text-infoText" />
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-infoText bg-infoBg px-2.5 py-1 rounded-full">
                        {svc.badge}
                      </span>
                    </div>
                    <h3 className="font-heading font-extrabold text-ink900 text-2xl tracking-tight mb-2">
                      {svc.title}
                    </h3>
                    <p className="text-sm text-ink700 leading-relaxed">
                      {svc.description}
                    </p>
                  </div>
                  <div className="lg:col-span-8 grid sm:grid-cols-2 gap-2.5 auto-rows-min content-start lg:border-l lg:border-line lg:pl-8">
                    {svc.items.map((item) => (
                      <div
                        key={item}
                        className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-surface border border-line/70 hover:bg-infoBg/60 transition-colors"
                      >
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="text-brandBlue mt-0.5 flex-shrink-0"
                        />
                        <span className="text-sm font-semibold text-ink900">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming soon grid */}
      <section className="py-24 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-warnText bg-warnBg px-3 py-1 rounded-full mb-3">
                <Sparkle size={12} weight="fill" />
                On the runway
              </div>
              <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl tracking-tight">
                Coming soon to JayesPay.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {UPCOMING_SERVICES.map((s, i) => {
              const Icon = iconMap[s.icon] || Sparkle;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03 }}
                  className="group relative bg-surface border border-line rounded-2xl p-5 hover:-translate-y-0.5 hover:border-lineStrong hover:bg-infoBg/40 transition-all"
                  data-testid={`upcoming-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-surfaceAlt border border-line flex items-center justify-center mb-3">
                    <Icon size={20} weight="duotone" className="text-infoText" />
                  </div>
                  <div className="text-sm font-bold text-ink900 leading-tight">
                    {s.title}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-warnText">
                    Coming Soon
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
